import React, { useState } from "react";

import { map } from "lodash/fp";

import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import SettingContent from "@app/analytics/account/components/SettingContent";
import SettingsModule from "@app/analytics/account/components/SettingsModule";
import { Divider } from "@app/analytics/account/components/styles";
import type { SettingModule } from "@app/analytics/account/types";
import PreferredPlatformSelector from "@app/analytics/components/PreferredPlatformSelector";
import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import RequireEditPermissionsConfirmationModal from "@app/analytics/Integrations/IntegrationDetail/CalendarIntegrationConfig/RequireEditPermissionsConfirmationModal";
import * as Events from "@app/analytics/tracking/events";
import type { ConnectedCalendarProvider } from "@app/common/auth/api";
import {
  CALENDAR_PROVIDERS_BY_ID,
  RSS_CALENDAR_PROVIDERS,
} from "@app/common/constants/providers";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { RssLinkPlatformPreference } from "@app/common/types";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import {
  formattedListWithConjunction,
  isResponseErrorWithStatus,
} from "@app/common/utils";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";
import Stack, { Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import Switch from "@rui/Switch";
import TooltipWrapper from "@rui/TooltipWrapper";

import AvailableHours from "./AvailableHours";
import CustomUrl from "./CustomUrl";
import MinimumSchedulingNotice from "./MinimumSchedulingNotice";
import SelectDefaultCalendarModal from "./SelectDefaultCalendarModal";

const SmartSchedulingModule = (props: SettingModule) => {
  const [defaultCalendarModalOpen, setDefaultCalendarModalOpen] =
    useState(false);
  const [
    pendingEditPermissionConfirmationProvider,
    setPendingEditPermissionConfirmationProvider,
  ] = useState<ConnectedCalendarProvider | null>(null);

  const { canConnectCalendars } = useFeatureAccess();

  const { connectCalendar } = useConnectCalendar();
  const {
    connectedRssCalendarProviders,
    hasConnectedRssCalendarProviders,
    rssCalendar,
    rssLinkSettings,
    updateRssLinkSettings,
    updateRssLinkSettingsSync,
  } = useRssLinksSettings();

  const toggleLinksEnabled = ({
    isEnabled,
    calendarProvider,
    updateFn = updateRssLinkSettings,
  }: {
    isEnabled: boolean;
    calendarProvider?: CalendarProviderId;
    updateFn?: typeof updateRssLinkSettingsSync;
  }) => {
    const body = calendarProvider
      ? { isEnabled, calendarProvider }
      : { isEnabled };

    updateFn.mutate(body, {
      onSettled: () => setDefaultCalendarModalOpen(false),
      onError: async (error) => {
        if (isResponseErrorWithStatus(error, 403)) {
          const json = await error.response.json();
          const calendarProviderDisplayName =
            CALENDAR_PROVIDERS_BY_ID[
              json.detail?.calendar_provider as CalendarProviderId
            ]?.displayName;

          toast.error({
            type: "system",
            title: "Authorization needed",
            message: `Your ${
              calendarProviderDisplayName ?? "calendar"
            } requires authorization to enable Smart Scheduler links.`,
            action: (
              <Link
                to={ROUTES.USER_SETTINGS_INTEGRATIONS}
                replace
                sx={{ pb: 0.5, color: "error.main" }}
              >
                Go to setting
              </Link>
            ),
          });
        } else {
          toast.error(
            `Something went wrong and Smart Scheduler links could not be ${
              isEnabled ? "enabled" : "disabled"
            }. Please try again later or reach out to support@read.ai if the problem persists.`
          );
        }
      },
    });
  };

  const handleConfirmDefaultCalendar = (
    calendarProvider: CalendarProviderId
  ) => {
    toggleLinksEnabled({
      // we only show the modal if they're trying to enable the links, so we
      // can just hard-code isEnabled to true
      isEnabled: true,
      calendarProvider,
      // don't optimistically update, since we'll show a loading state in the modal
      updateFn: updateRssLinkSettingsSync,
    });
  };

  const handleToggleLinksEnabled = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    checked ? Events.enableRssLinks() : Events.disableRssLinks();

    let calendarProvider: CalendarProviderId | undefined;

    if (checked && !rssCalendar) {
      if (connectedRssCalendarProviders.length === 1) {
        // if user is trying to enable RSS Links and does not currently have a
        // default calendar set, select the calendar automatically if they only
        // have one provider connected that is usable with RSS
        calendarProvider = connectedRssCalendarProviders[0].providerId;
      } else {
        // otherwise, they need to manually select which calendar they want to use
        setDefaultCalendarModalOpen(true);
        return;
      }
    }

    toggleLinksEnabled({ isEnabled: checked, calendarProvider });
  };

  const handleChangePlatform = (
    platformPreference: RssLinkPlatformPreference
  ) => {
    updateRssLinkSettings.mutate({ platformPreference });
  };

  const handleSetAsDefaultCalendar = (provider: ConnectedCalendarProvider) => {
    if (provider.providerId === rssCalendar?.provider) return;

    const providerConfig = CALENDAR_PROVIDERS_BY_ID[provider.providerId];
    updateRssLinkSettings.mutate(
      { calendarProvider: provider.providerId },
      {
        onError: (error) => {
          if (isResponseErrorWithStatus(error, 403)) {
            setPendingEditPermissionConfirmationProvider(provider);
          } else if (isResponseErrorWithStatus(error, 422)) {
            toast.error(
              `${providerConfig.displayName} default calendar is disabled. Please enable your ${providerConfig.displayName} default calendar.`
            );
          } else {
            toast.error(
              "Something went wrong and your default calendar could not be updated. Please try again later or reach out to support@read.ai if the problem persists."
            );
          }
        },
      }
    );
  };

  const handleConnectCalendar = (calendarProviderId: CalendarProviderId) =>
    connectCalendar(calendarProviderId, {
      returnTo: ROUTES.USER_SETTINGS_SMART_SCHEDULER,
    });

  const enableRssLinksDisabled =
    !rssLinkSettings.isEnabled &&
    (!hasConnectedRssCalendarProviders || !canConnectCalendars);

  return (
    <>
      <SettingsModule {...props} sx={{ color: "shades.neutral.800" }}>
        <Divider />
        <Stack sx={{ px: 1 }} spacing={1.5}>
          <Row variant="apart">
            <Typography variant="h3">Smart Scheduler links</Typography>
            <TooltipWrapper
              show={enableRssLinksDisabled}
              title={
                canConnectCalendars
                  ? "Connect a calendar to enable Smart Scheduler Links"
                  : "Enforced by your Workspace"
              }
            >
              <Switch
                disabled={enableRssLinksDisabled}
                withStatusLabel
                checked={rssLinkSettings.isEnabled}
                onChange={handleToggleLinksEnabled}
              />
            </TooltipWrapper>
          </Row>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Smart Scheduler links allow others to find a time to meet with you
            via a scheduling link. When enabled, your links can be managed in{" "}
            <Link to={ROUTES.CALENDAR_SCHEDULING}>Calendar</Link>.
          </Typography>
          {!hasConnectedRssCalendarProviders && (
            <InfoPanel severity="error">
              Smart Scheduler links require a{" "}
              {formattedListWithConjunction(
                map("displayName", RSS_CALENDAR_PROVIDERS),
                "or"
              )}{" "}
              connection with at least one enabled calendar.{" "}
              <Link to={ROUTES.INTEGRATIONS_DEFAULT_SECTION} replace>
                Go to integrations
              </Link>
              .
            </InfoPanel>
          )}
          <Collapse unmountOnExit in={rssLinkSettings.isEnabled}>
            <SettingContent spacing={2}>
              <Divider />
              <Stack spacing={1.5}>
                <Stack spacing={1.5}>
                  <Typography variant="bodyPrimaryMed">
                    Scheduling Calendar
                  </Typography>
                  <Typography
                    variant="bodySecondaryReg"
                    color="shades.neutral.600"
                  >
                    This calendar will be used to find availability and schedule
                    events
                  </Typography>
                </Stack>
                <Row flex={1} maxWidth="500px">
                  <Dropdown
                    name="default-scheduling-calendar"
                    selectedItem={
                      rssCalendar
                        ? {
                            value: rssCalendar.provider,
                            label:
                              CALENDAR_PROVIDERS_BY_ID[rssCalendar.provider]
                                .displayName,
                          }
                        : undefined
                    }
                    wrapperSx={{ width: "100%" }}
                  >
                    {connectedRssCalendarProviders.map((provider) => (
                      <DropdownMenuItem
                        key={provider.providerId}
                        label={
                          CALENDAR_PROVIDERS_BY_ID[provider.providerId]
                            .displayName
                        }
                        onClick={() => handleSetAsDefaultCalendar(provider)}
                        value={provider.providerId}
                      />
                    ))}
                  </Dropdown>
                </Row>
              </Stack>
              <Divider />
              <Stack spacing={1.5}>
                <Stack spacing={1.5}>
                  <Typography variant="bodyPrimaryMed">
                    Default conferencing platform
                  </Typography>
                  <Typography
                    variant="bodySecondaryReg"
                    color="shades.neutral.600"
                  >
                    Meetings scheduled with Smart Scheduler will use this
                    platform by default
                  </Typography>
                </Stack>
                <Row flex={1} maxWidth="500px">
                  <PreferredPlatformSelector
                    onChange={handleChangePlatform}
                    onConnectCalendar={handleConnectCalendar}
                    platformOptions={rssLinkSettings.platformOptions}
                    selected={rssLinkSettings.platformPreference}
                    wrapperSx={{ width: "100%" }}
                  />
                </Row>
              </Stack>
            </SettingContent>
          </Collapse>
        </Stack>
        <Stack spacing={2}>
          <Divider />
          <CustomUrl />
          <Divider />
          <Stack sx={{ px: 1 }} spacing={1}>
            <Stack spacing={1.5}>
              <Typography variant="h3">Calendar & Availability</Typography>
              <Typography variant="bodySecondaryReg" color="shades.neutral.600">
                Customize your calendar settings for Smart Scheduler.
              </Typography>
              {rssCalendar && (
                <InfoPanel severity="info">
                  Smart Scheduler is using{" "}
                  <Bold>
                    {rssCalendar.summary} (
                    {CALENDAR_PROVIDERS_BY_ID[rssCalendar.provider].displayName}
                    )
                  </Bold>{" "}
                  to display availability, schedule, set available hours, and
                  determine time zone.
                </InfoPanel>
              )}
            </Stack>
            <AvailableHours
              settings={rssLinkSettings}
              onSettingUpdate={updateRssLinkSettings.mutate}
            />
            <Divider />
            <MinimumSchedulingNotice
              settings={rssLinkSettings}
              onSettingUpdate={updateRssLinkSettings.mutate}
            />
          </Stack>
        </Stack>
      </SettingsModule>

      {pendingEditPermissionConfirmationProvider != null && (
        <RequireEditPermissionsConfirmationModal
          calendarDisplayName={
            CALENDAR_PROVIDERS_BY_ID[
              pendingEditPermissionConfirmationProvider.providerId
            ].displayName
          }
          open
          onClose={() => setPendingEditPermissionConfirmationProvider(null)}
          onConfirm={() =>
            handleConnectCalendar(
              pendingEditPermissionConfirmationProvider?.providerId
            )
          }
        />
      )}
      <SelectDefaultCalendarModal
        open={defaultCalendarModalOpen}
        onClose={() => setDefaultCalendarModalOpen(false)}
        onConfirm={handleConfirmDefaultCalendar}
        loading={updateRssLinkSettingsSync.isPending}
        calendarProviders={connectedRssCalendarProviders}
      />
    </>
  );
};

export default SmartSchedulingModule;
